"use client";

import { c as _c } from "react/compiler-runtime";
import { Alert as HeroAlert } from "@heroui/react";
import type { ReactNode } from "react";
import { CheckCircleBold, DangerTriangleBold, InfoCircleBold } from "solar-icon-set";
import { cn } from "../../lib/utils";
import { DangerHexagonIcon } from "../../vectors/DangerHexagonIcon";
type AlertColor = "default" | "primary" | "secondary" | "success" | "warning" | "danger";
const defaultIconMap: Record<AlertColor, ReactNode> = {
  default: <InfoCircleBold size={24} />,
  primary: <InfoCircleBold size={24} />,
  secondary: <InfoCircleBold size={24} />,
  success: <CheckCircleBold size={24} />,
  warning: <DangerTriangleBold size={24} />,
  danger: <DangerHexagonIcon size={24} />
};
const colorClassNames: Record<AlertColor, Record<string, string>> = {
  default: {
    base: "bg-[var(--color-bg-container-surface-usual)] text-foreground gap-4 p-3 rounded-2xl",
    title: "text-sm font-medium leading-5",
    description: "text-sm font-normal leading-5 text-[var(--color-text-body-neutral-onMatch-muted)]",
    closeButton: "text-[var(--color-text-body-neutral-onMatch-muted)]"
  },
  primary: {
    base: "bg-[var(--color-bg-container-primary-dimmed)] text-[var(--color-text-body-primary)] gap-4 p-3 rounded-2xl",
    title: "text-sm font-medium leading-5",
    description: "text-sm font-normal leading-5",
    closeButton: "text-[var(--color-text-body-primary)]"
  },
  secondary: {
    base: "bg-[var(--color-bg-container-secondary-dimmed)] text-[var(--color-text-body-secondary)] gap-4 p-3 rounded-2xl",
    title: "text-sm font-medium leading-5",
    description: "text-sm font-normal leading-5",
    closeButton: "text-[var(--color-text-body-secondary)]"
  },
  success: {
    base: "bg-[var(--color-bg-container-success-dimmed)] text-[var(--color-text-body-success)] gap-4 p-3 rounded-2xl",
    title: "text-sm font-medium leading-5",
    description: "text-sm font-normal leading-5",
    closeButton: "text-[var(--color-text-body-success)]"
  },
  warning: {
    base: "bg-[var(--color-bg-container-warning-dimmed)] text-[var(--color-text-body-warning)] gap-4 p-3 rounded-2xl",
    title: "text-sm font-medium leading-5",
    description: "text-sm font-normal leading-5",
    closeButton: "text-[var(--color-text-body-warning)]"
  },
  danger: {
    base: "bg-[var(--color-bg-container-danger-dimmed)] text-[var(--color-text-body-danger)] gap-4 p-3 rounded-2xl",
    title: "text-sm font-medium leading-5",
    description: "text-sm font-normal leading-5",
    closeButton: "text-[var(--color-text-body-danger)]"
  }
};
export const Alert = t0 => {
  const $ = _c(43);
  let className;
  let classNames;
  let description;
  let icon;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      classNames,
      color: t1,
      description,
      icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = classNames;
    $[3] = description;
    $[4] = icon;
    $[5] = props;
    $[6] = t1;
  } else {
    className = $[1];
    classNames = $[2];
    description = $[3];
    icon = $[4];
    props = $[5];
    t1 = $[6];
  }
  const color = t1 === undefined ? "default" : t1;
  const resolvedColor = (color ?? "default") as AlertColor;
  const resolvedIcon = icon ?? defaultIconMap[resolvedColor];
  const figmaClasses = colorClassNames[resolvedColor];
  const hasDescription = description != null && description !== "";
  let t2;
  if ($[7] !== className) {
    t2 = cn(className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = hasDescription ? "items-start" : "items-center";
  const t4 = classNames?.base;
  let t5;
  if ($[9] !== figmaClasses.base || $[10] !== t3 || $[11] !== t4) {
    t5 = cn(figmaClasses.base, t3, t4);
    $[9] = figmaClasses.base;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = classNames?.iconWrapper;
  let t7;
  if ($[13] !== t6) {
    t7 = cn("h-6 w-6 border-none! bg-transparent! shadow-none!", t6);
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = classNames?.alertIcon;
  let t9;
  if ($[15] !== t8) {
    t9 = cn("h-6 w-6", t8);
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = classNames?.title;
  let t11;
  if ($[17] !== figmaClasses.title || $[18] !== t10) {
    t11 = cn(figmaClasses.title, t10);
    $[17] = figmaClasses.title;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = classNames?.description;
  let t13;
  if ($[20] !== figmaClasses.description || $[21] !== t12) {
    t13 = cn(figmaClasses.description, t12);
    $[20] = figmaClasses.description;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = classNames?.closeButton;
  let t15;
  if ($[23] !== figmaClasses.closeButton || $[24] !== t14) {
    t15 = cn(figmaClasses.closeButton, t14);
    $[23] = figmaClasses.closeButton;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const t16 = classNames?.mainWrapper;
  let t17;
  if ($[26] !== t16) {
    t17 = cn("ms-0", t16);
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t11 || $[29] !== t13 || $[30] !== t15 || $[31] !== t17 || $[32] !== t5 || $[33] !== t7 || $[34] !== t9) {
    t18 = {
      base: t5,
      iconWrapper: t7,
      alertIcon: t9,
      title: t11,
      description: t13,
      closeButton: t15,
      mainWrapper: t17
    };
    $[28] = t11;
    $[29] = t13;
    $[30] = t15;
    $[31] = t17;
    $[32] = t5;
    $[33] = t7;
    $[34] = t9;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== color || $[37] !== description || $[38] !== props || $[39] !== resolvedIcon || $[40] !== t18 || $[41] !== t2) {
    t19 = <HeroAlert className={t2} description={description} classNames={t18} color={color} icon={resolvedIcon} {...props} />;
    $[36] = color;
    $[37] = description;
    $[38] = props;
    $[39] = resolvedIcon;
    $[40] = t18;
    $[41] = t2;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  return t19;
};